import { ref } from '@vue/composition-api'
import axios from '@axios'
import { memberGuid, normalizeMember } from './memberRowId'

export default function useNotEnrolledMembers({
  courseId,
  fullList,
  fetchCourseSubscribers,
}) {
  const notEnrolledCache = ref(null)
  const isFetchingNotEnrolled = ref(false)
  const loadProgress = ref(0)

  function applyNotEnrolledFilter() {
    const cache = notEnrolledCache.value
    if (!cache) {
      fullList.value = []
      return
    }
    const { allMembers, enrolledGuids } = cache
    fullList.value = allMembers.filter(m => m.id && !enrolledGuids[m.id])
  }

  async function loadNotEnrolledList({ onError }) {
    if (!courseId.value) return

    if (notEnrolledCache.value && notEnrolledCache.value.courseId === courseId.value) {
      applyNotEnrolledFilter()
      return
    }

    isFetchingNotEnrolled.value = true
    loadProgress.value = 0

    try {
      loadProgress.value = 20
      const [membersRes, subscribersRes] = await Promise.all([
        axios.get('/admin/f/cm/getallmembers'),
        fetchCourseSubscribers(),
      ])
      loadProgress.value = 80

      const membersPayload = membersRes.data || {}
      const rawMembers = membersPayload.users || membersPayload.data || []
      const subscribers = subscribersRes || []
      const allMembers = rawMembers.map(m => normalizeMember(m))
      const enrolledGuids = Object.create(null)

      subscribers.forEach(s => {
        const guid = memberGuid(s)
        if (guid) enrolledGuids[guid] = true
      })

      notEnrolledCache.value = {
        courseId: courseId.value,
        allMembers,
        enrolledGuids,
      }
      loadProgress.value = 100
      applyNotEnrolledFilter()
    } catch (e) {
      if (onError) onError(e)
      loadProgress.value = 0
    } finally {
      isFetchingNotEnrolled.value = false
    }
  }

  function markMembersAsEnrolled(members, { notEnrolledOnly, clearSelection }) {
    const cache = notEnrolledCache.value
    if (!cache) return

    ;(members || []).forEach(m => {
      const guid = memberGuid(m)
      if (guid) cache.enrolledGuids[guid] = true
    })

    if (notEnrolledOnly.value) {
      applyNotEnrolledFilter()
      if (clearSelection) clearSelection()
    }
  }

  function resetNotEnrolledState() {
    isFetchingNotEnrolled.value = false
    loadProgress.value = 0
  }

  return {
    notEnrolledCache,
    isFetchingNotEnrolled,
    loadProgress,
    loadNotEnrolledList,
    applyNotEnrolledFilter,
    markMembersAsEnrolled,
    resetNotEnrolledState,
  }
}
